import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CHANGELOG_KEY_HEADER } from "~/data/changelog-config";
import type { Changelog } from "~/data/changelog-schema";
import type { ChangelogMode } from "~/stores/changelog";

export interface SaveChangelogResult extends Changelog {
	/** False when the data was valid but the server filesystem is read-only. */
	persisted?: boolean;
}

async function fetchChangelog(
	mode: ChangelogMode,
	passkey: string | null
): Promise<Changelog> {
	const url = mode === "edit" ? "/api/changelog?mode=edit" : "/api/changelog";
	const headers: Record<string, string> = {};
	if (mode === "edit" && passkey) {
		headers[CHANGELOG_KEY_HEADER] = passkey;
	}
	const response = await fetch(url, { headers });
	if (!response.ok) {
		throw new Error(`Failed to load changelog (${response.status})`);
	}
	return (await response.json()) as Changelog;
}

/** Load the changelog. In `edit` mode the passkey unlocks internal entries. */
export function useChangelog(
	mode: ChangelogMode,
	passkey: string | null,
	enabled: boolean
) {
	return useQuery({
		queryKey: ["changelog", mode],
		queryFn: () => fetchChangelog(mode, passkey),
		enabled,
		staleTime: 60_000,
	});
}

/** Persist the full changelog (passkey required). */
export function useSaveChangelog(passkey: string | null) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (data: Changelog): Promise<SaveChangelogResult> => {
			const headers: Record<string, string> = {
				"content-type": "application/json",
			};
			if (passkey) {
				headers[CHANGELOG_KEY_HEADER] = passkey;
			}
			const response = await fetch("/api/changelog", {
				method: "POST",
				headers,
				body: JSON.stringify(data),
			});
			const json = (await response.json()) as SaveChangelogResult & {
				error?: string;
			};
			if (!response.ok) {
				throw new Error(json.error ?? `Save failed (${response.status})`);
			}
			return json;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["changelog"] });
		},
	});
}
